import { JourneyPerformanceReport } from './revision-insights'

/**
 * The reports that render as a table.
 *
 * Split Test Variants renders as a comparison of cards instead, so nothing in this
 * file describes it: a card has no horizontal axis for a column model to lay out,
 * and its Variant % reads as an allocation line rather than a column.
 */
export type JourneyInsightsTableReport = Exclude<JourneyPerformanceReport, 'SPLIT_TEST_VARIANTS'>

/**
 * Every column the Insights tables can render, keyed by the identity antd uses for
 * it. A report's column set is a subset of these in this order.
 */
export type InsightsColumnKey =
    | 'notification'
    | 'step'
    | 'entrances'
    | 'impressions'
    | 'clicks'
    | 'purchases'
    | 'revenue'

/**
 * The leading columns naming the row, per report. These come first and the first
 * of them is pinned left, so a reader keeps their bearings if the metrics ever
 * scroll sideways.
 */
const DIMENSION_COLUMN_KEYS: Record<JourneyInsightsTableReport, InsightsColumnKey[]> = {
    NOTIFICATIONS: ['notification'],
    STEPS: ['step'],
}

/**
 * Reports whose rows represent something subscribers enter. A notification is
 * sent rather than entered, so the Notifications report has no entrant count.
 */
const REPORTS_WITH_ENTRANTS: JourneyInsightsTableReport[] = ['STEPS']

/**
 * Fixed pixel widths for the metric block. The dimension column carries free text
 * — a notification title, a step name — so it takes no width here and absorbs
 * whatever space the metrics leave.
 */
export const INSIGHTS_METRIC_COLUMN_WIDTHS: Record<string, number> = {
    entrances: 92,
    impressions: 104,
    clicks: 112,
    purchases: 116,
    revenue: 124,
}

/**
 * The columns a report renders, in render order, for the given domain.
 *
 * This is the single source of truth for the column set: the table maps over the
 * returned keys to build its columns, and the merged revision group-header row
 * spans exactly this many cells. Deriving both from one list is what keeps the
 * header's colSpan from drifting out of step with the columns beside it.
 */
export function buildInsightsColumnKeys(
    report: JourneyInsightsTableReport,
    showPurchaseColumns: boolean,
): InsightsColumnKey[] {
    const keys: InsightsColumnKey[] = [...DIMENSION_COLUMN_KEYS[report]]

    if (REPORTS_WITH_ENTRANTS.includes(report)) {
        keys.push('entrances')
    }

    keys.push('impressions', 'clicks')

    if (showPurchaseColumns) {
        keys.push('purchases', 'revenue')
    }

    return keys
}

/**
 * How many cells a full-width row in this report spans — the colSpan the merged
 * revision group-header and per-group empty-note cells carry.
 */
export function resolveInsightsColumnCount(
    report: JourneyInsightsTableReport,
    showPurchaseColumns: boolean,
): number {
    return buildInsightsColumnKeys(report, showPurchaseColumns).length
}
